import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router-dom';
import {
  Plus,
  ArrowUpRight,
  ArrowDownLeft,
  RefreshCw,
  Archive,
  Search,
  ShoppingCart,
  Undo2,
  Trash2,
  ClipboardList,
  ArrowRightLeft,
  Download,
  Lock,
  X,
} from 'lucide-react';
import {
  getInventoryMovements,
  createInventoryMovement,
  getInventoryItems,
  listRestaurants,
} from '../services/api';
import CreateMovementModal from '../components/CreateMovementModal';
import TransferStockModal from '../components/TransferStockModal';
import ConsumptionIssuesPanel from '../components/ConsumptionIssuesPanel';
import {
  MOVEMENT_TYPES,
  TYPE_LABEL_KEYS,
  MAX_EXPORT_ROWS,
  typeBadgeClass,
  normalizeMovementsResponse,
  movementRefLink,
  movementQueryParams,
  reasonCodeLabelKey,
  transferCounterpart,
} from '../utils/movements';
import { kardexSummary, movementsToCsv, csvFileName, downloadCsv } from '../utils/kardex';
import { useSubscription } from '../context/SubscriptionContext';
import { evaluateFeatureGate, openFeatureGate } from '../config/featureGates';
import { storage } from '../utils/storage';
import './InventoryMovementsPage.css';

const PAGE_SIZE = 50;
// El export pide páginas más grandes y corta en MAX_EXPORT_ROWS filas (RES-41), avisando
const EXPORT_PAGE_SIZE = 200;

const EMPTY_FILTERS = { itemId: '', type: '', refId: '', from: '', to: '' };

/**
 * Filtros que llegan por query string. Es lo que hace andar el circuito "veo un consumo con
 * problemas → corrijo → reintento": los links del panel de consumos y del detalle del pedido
 * apuntan acá con `?itemId=` o `?refId=`, y hasta RES-57 esta pantalla los ignoraba y arrancaba
 * siempre sin filtros.
 * @param {URLSearchParams} params - Query string
 * @returns {Object} Filtros
 */
function filtersFromParams(params) {
  return {
    itemId: params.get('itemId') || '',
    type: params.get('type') || '',
    refId: params.get('refId') || '',
    from: params.get('from') || '',
    to: params.get('to') || '',
  };
}

const sameFilters = (a, b) => Object.keys(EMPTY_FILTERS).every((key) => a[key] === b[key]);

/**
 * Historial de movimientos del restaurante seleccionado (RES-24): filtros por insumo, tipo,
 * pedido y rango de fechas resueltos en el backend, paginación por token, badge por tipo y link
 * al pedido u orden de compra de origen.
 * @returns {JSX.Element} Página
 */
function InventoryMovementsPage() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const [movements, setMovements] = useState([]);
  const [nextToken, setNextToken] = useState(null);
  const [items, setItems] = useState([]);
  // RES-70: catálogo aparte (con inactivos) solo para resolver nombres en la tabla de movimientos.
  // `items` sigue siendo activos-únicamente: alimenta el picker del modal "crear movimiento" y un
  // insumo desactivado no se debe poder elegir ahí (RES-22).
  const [displayItems, setDisplayItems] = useState([]);
  const [restaurants, setRestaurants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  // RES-44: transferencia entre locales, con el local seleccionado precargado como origen
  const [isTransferOpen, setIsTransferOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportNotice, setExportNotice] = useState('');
  const [filters, setFilters] = useState(() => filtersFromParams(searchParams));
  const [feedback, setFeedback] = useState('');

  // El link puede llegar estando ya en esta pantalla (React Router no remonta la página), así
  // que los filtros siguen a la URL. Solo se pisan si cambiaron: si no, se dispara otro fetch.
  const search = searchParams.toString();
  useEffect(() => {
    const next = filtersFromParams(new URLSearchParams(search));
    setFilters((prev) => (sameFilters(prev, next) ? prev : next));
  }, [search]);

  const currentRestaurantId = storage.get('selectedRestaurantId');
  // Local cuyos movimientos se miran (RES-41): en una cadena se puede consultar otro local sin
  // cambiar el seleccionado. Vacío = el local seleccionado.
  const [viewRestaurantId, setViewRestaurantId] = useState('');
  const viewingOtherLocal = Boolean(viewRestaurantId) && viewRestaurantId !== currentRestaurantId;
  const { hasFeature, initialized: subscriptionInitialized, subscription } = useSubscription();
  // El export es del plan Profesional en adelante (gate `dataExportEnabled`)
  const canExport = subscriptionInitialized && hasFeature('dataExport');

  const queryParams = useCallback(
    (token, limit = PAGE_SIZE) => movementQueryParams(filters, token, limit),
    [filters]
  );

  // El header X-Restaurant-Id solo se pisa para mirar otro local
  const fetchPage = useCallback(
    (token, limit) => {
      const params = queryParams(token, limit);
      return viewingOtherLocal
        ? getInventoryMovements(params, { restaurantId: viewRestaurantId })
        : getInventoryMovements(params);
    },
    [queryParams, viewingOtherLocal, viewRestaurantId]
  );

  // RES-72: cada cambio de filtro dispara su consulta y pueden volver fuera de orden (la de
  // insumo + tipo lee más páginas en el backend que la de solo insumo). Solo se aplica la
  // respuesta de la última consulta; las viejas se descartan.
  const requestSeq = useRef(0);

  const fetchMovements = useCallback(async () => {
    const seq = ++requestSeq.current;
    setLoading(true);
    try {
      const data = await fetchPage(null);
      if (seq !== requestSeq.current) return;
      const page = normalizeMovementsResponse(data);
      setMovements(page.items);
      setNextToken(page.nextToken);
    } catch (error) {
      if (seq !== requestSeq.current) return;
      console.error('Error fetching movements:', error);
      setMovements([]);
      setNextToken(null);
    } finally {
      if (seq === requestSeq.current) setLoading(false);
    }
  }, [fetchPage]);

  const loadMore = async () => {
    if (!nextToken) return;
    const seq = requestSeq.current;
    setLoadingMore(true);
    try {
      const page = normalizeMovementsResponse(await fetchPage(nextToken));
      // Si mientras tanto cambiaron los filtros, esta página es de la consulta anterior
      if (seq !== requestSeq.current) return;
      setMovements((prev) => [...prev, ...page.items]);
      setNextToken(page.nextToken);
    } catch (error) {
      console.error('Error fetching more movements:', error);
    } finally {
      setLoadingMore(false);
    }
  };

  useEffect(() => {
    // Catálogo con stock del local y restaurantes de la org (destino de transferencias)
    const load = async () => {
      try {
        const [itemsData, displayItemsData, restaurantsData] = await Promise.all([
          getInventoryItems(),
          getInventoryItems({ includeInactive: true }),
          listRestaurants().catch(() => []),
        ]);
        setItems(itemsData?.items || []);
        setDisplayItems(displayItemsData?.items || []);
        const list = Array.isArray(restaurantsData)
          ? restaurantsData
          : restaurantsData?.restaurants || [];
        setRestaurants(list);
      } catch (error) {
        console.error('Error fetching inventory data:', error);
      }
    };
    load();
  }, []);

  useEffect(() => {
    fetchMovements();
  }, [fetchMovements]);

  useEffect(() => {
    if (!feedback) return undefined;
    const timer = setTimeout(() => setFeedback(''), 4000);
    return () => clearTimeout(timer);
  }, [feedback]);

  const handleCreateMovement = async (movementData) => {
    await createInventoryMovement(movementData);
    setFeedback(t('inventory.movementsPage.saveSuccess'));
    const [itemsData] = await Promise.all([getInventoryItems(), fetchMovements()]);
    setItems(itemsData?.items || []);
  };

  const handleTransferred = async () => {
    setFeedback(t('inventory.transfers.created'));
    try {
      const [itemsData] = await Promise.all([getInventoryItems(), fetchMovements()]);
      setItems(itemsData?.items || []);
    } catch (error) {
      console.error('Error refreshing after transfer:', error);
    }
  };

  const setFilter = (name, value) => setFilters((prev) => ({ ...prev, [name]: value }));

  const activeRestaurants = restaurants.filter((r) => r.restaurantId && r.status !== 'deleted');
  const isChain = activeRestaurants.length > 1;

  const getMovementIcon = (type) => {
    switch (type) {
      case 'STOCK_IN':
        return <ArrowDownLeft size={20} className="icon-in" />;
      case 'STOCK_OUT':
        return <ArrowUpRight size={20} className="icon-out" />;
      case 'ADJUSTMENT':
        return <RefreshCw size={20} className="icon-adj" />;
      case 'WASTE':
        return <Trash2 size={20} className="icon-waste" />;
      case 'COUNT':
        return <ClipboardList size={20} className="icon-count" />;
      case 'TRANSFER_OUT':
      case 'TRANSFER_IN':
        return <ArrowRightLeft size={20} className="icon-transfer" />;
      case 'SALE':
        return <ShoppingCart size={20} className="icon-sale" />;
      case 'SALE_REVERSAL':
        return <Undo2 size={20} className="icon-reversal" />;
      default:
        return <Archive size={20} />;
    }
  };

  const getMovementLabel = (type) =>
    TYPE_LABEL_KEYS[type] ? t(`inventory.movementsPage.${TYPE_LABEL_KEYS[type]}`) : type;

  const getItemName = (itemId) => {
    const item = displayItems.find((i) => i.itemId === itemId);
    return item ? item.name : t('inventory.movementsPage.unknownItem');
  };

  const getItemUnit = (itemId) => {
    const item = displayItems.find((i) => i.itemId === itemId);
    return item ? item.unit : '';
  };

  // Kardex del insumo elegido (B6 / RES-34): el saldo corriente ya viene en cada movimiento
  // (`StockBefore` / `StockAfter`), así que acá solo se resume y se contrasta con el stock de hoy.
  // RES-70: displayItems (con inactivos) — un link con ?itemId= a un insumo ya desactivado no
  // tiene por qué perder el kardex, mismo motivo que getItemName/getItemUnit arriba.
  const selectedItem = filters.itemId
    ? displayItems.find((i) => i.itemId === filters.itemId)
    : null;
  // Con filtro de fechas, tipo o pedido el historial queda cortado por arriba: el cierre no
  // tiene por qué coincidir. Y si quedan páginas sin cargar (RES-56), el saldo inicial y el neto
  // son de la ventana que está en pantalla, no del período: hay que decirlo.
  const kardexTruncatedByPaging = Boolean(selectedItem) && Boolean(nextToken);
  const kardexIsComplete =
    Boolean(selectedItem) &&
    !viewingOtherLocal &&
    !filters.from &&
    !filters.to &&
    !filters.type &&
    !filters.refId &&
    !nextToken;
  const kardex = selectedItem
    ? kardexSummary(movements, kardexIsComplete ? (selectedItem.stock ?? null) : null)
    : null;

  /**
   * Exporta el período filtrado completo, no la ventana cargada (RES-56): recorre `nextToken`
   * hasta agotarlo antes de generar el archivo. El nombre del archivo dice `desde-`/`hasta-`, así
   * que exportar solo las 50 filas en pantalla daba un CSV que parecía del período y no lo era.
   * @returns {Promise<void>}
   */
  const handleExportCsv = async () => {
    // Sin el plan no se esconde el botón: se abre el modal de upgrade (RES-41)
    if (!canExport) {
      openFeatureGate(evaluateFeatureGate('dataExportEnabled', subscription));
      return;
    }
    setExporting(true);
    setExportNotice('');
    try {
      const all = [...movements];
      let token = nextToken;
      while (token && all.length < MAX_EXPORT_ROWS) {
        const page = normalizeMovementsResponse(await fetchPage(token, EXPORT_PAGE_SIZE));
        all.push(...page.items);
        token = page.nextToken;
      }
      const truncated = Boolean(token) || all.length > MAX_EXPORT_ROWS;
      const rows = all.slice(0, MAX_EXPORT_ROWS);
      const csv = movementsToCsv(rows, { itemName: getItemName, itemUnit: getItemUnit });
      downloadCsv(csv, csvFileName(filters, new Date(), getItemName(filters.itemId)));
      if (truncated) {
        setExportNotice(t('inventory.movementsPage.exportTruncated', { count: rows.length }));
      }
    } catch (error) {
      console.error('Error exporting movements:', error);
      setExportNotice(t('inventory.movementsPage.exportFailed'));
    } finally {
      setExporting(false);
    }
  };

  const getRestaurantName = (restaurantId) => {
    const restaurant = restaurants.find((r) => r.restaurantId === restaurantId);
    return restaurant?.name || restaurantId;
  };

  /**
   * Motivo: otro local de la transferencia, motivo codificado (merma / stock inicial) y link al
   * origen (pedido / orden de compra / transferencia) o el texto libre.
   */
  const renderReason = (movement) => {
    const link = movementRefLink(movement);
    const counterpart = transferCounterpart(movement);
    const codeKey = reasonCodeLabelKey(movement);
    const codeLabel = codeKey ? t(codeKey) : null;
    return (
      <>
        {counterpart && (
          <span className="movement-counterpart">
            {counterpart.direction === 'to' ? '→ ' : '← '}
            {getRestaurantName(counterpart.restaurantId)}
          </span>
        )}
        {codeLabel && <span className="movement-reason-code">{codeLabel}</span>}
        {link ? (
          <Link className="movement-order-link" to={link.to} title={t(link.labelKey)}>
            {movement.Reason || t(link.labelKey)}
          </Link>
        ) : movement.Reason ? (
          <span>{movement.Reason}</span>
        ) : (
          !counterpart && !codeLabel && '-'
        )}
      </>
    );
  };

  const hasFilters =
    filters.itemId ||
    filters.type ||
    filters.refId ||
    filters.from ||
    filters.to ||
    viewingOtherLocal;

  return (
    <div className="movements-page">
      <div className="page-header">
        <div>
          <h1>{t('inventory.movementsPage.title')}</h1>
          <p className="subtitle">{t('inventory.movementsPage.subtitle')}</p>
        </div>
        <div className="page-header-actions">
          {canExport && (
            <button
              type="button"
              className="btn btn-secondary movements-export-btn"
              onClick={handleExportCsv}
              disabled={movements.length === 0 || exporting}
            >
              <Download size={18} aria-hidden="true" />
              {exporting
                ? t('inventory.movementsPage.exporting')
                : t('inventory.movementsPage.exportCsv')}
            </button>
          )}
          {subscriptionInitialized && !canExport && (
            <button
              type="button"
              className="btn btn-secondary movements-export-btn movements-export-btn--locked"
              onClick={handleExportCsv}
              title={t('inventory.movementsPage.exportLocked')}
            >
              <Lock size={16} aria-hidden="true" />
              {t('inventory.movementsPage.exportCsv')}
            </button>
          )}
          {isChain && (
            <button
              type="button"
              className="btn btn-secondary movements-transfer-btn"
              onClick={() => setIsTransferOpen(true)}
            >
              <ArrowRightLeft size={18} aria-hidden="true" />
              {t('inventory.transfers.transferAction')}
            </button>
          )}
          {/* Los movimientos manuales van al local seleccionado: mirando otro no se registran */}
          {!viewingOtherLocal && (
            <button type="button" className="primary-button" onClick={() => setIsModalOpen(true)}>
              <Plus size={20} />
              {t('inventory.movementsPage.registerMovement')}
            </button>
          )}
        </div>
      </div>

      {feedback && (
        <div className="alert alert-success" role="status" aria-live="polite">
          {feedback}
        </div>
      )}

      {exportNotice && (
        <p className="movements-export-notice" role="status">
          {exportNotice}
        </p>
      )}

      <ConsumptionIssuesPanel />

      <div className="filters-bar">
        {isChain && (
          <select
            aria-label={t('inventory.movementsPage.restaurant')}
            value={viewRestaurantId || currentRestaurantId || ''}
            onChange={(e) =>
              setViewRestaurantId(e.target.value === currentRestaurantId ? '' : e.target.value)
            }
            className="restaurant-filter-select"
          >
            {activeRestaurants.map((r) => (
              <option key={r.restaurantId} value={r.restaurantId}>
                {r.name || r.restaurantId}
              </option>
            ))}
          </select>
        )}
        <div className="search-box">
          <Search size={18} />
          <select
            aria-label={t('inventory.movementsPage.product')}
            value={filters.itemId}
            onChange={(e) => setFilter('itemId', e.target.value)}
            className="item-filter-select"
          >
            <option value="">{t('inventory.movementsPage.allProducts')}</option>
            {items.map((item) => (
              <option key={item.itemId} value={item.itemId}>
                {item.name}
              </option>
            ))}
          </select>
        </div>
        <select
          aria-label={t('inventory.movementsPage.type')}
          value={filters.type}
          onChange={(e) => setFilter('type', e.target.value)}
          className="type-filter-select"
        >
          <option value="">{t('inventory.movementsPage.allTypes')}</option>
          {MOVEMENT_TYPES.map((type) => (
            <option key={type} value={type}>
              {getMovementLabel(type)}
            </option>
          ))}
        </select>
        <label className="date-filter">
          <span>{t('inventory.movementsPage.from')}</span>
          <input
            type="date"
            value={filters.from}
            max={filters.to || undefined}
            onChange={(e) => setFilter('from', e.target.value)}
          />
        </label>
        <label className="date-filter">
          <span>{t('inventory.movementsPage.to')}</span>
          <input
            type="date"
            value={filters.to}
            min={filters.from || undefined}
            onChange={(e) => setFilter('to', e.target.value)}
          />
        </label>
        {filters.refId && (
          <span className="movement-ref-filter">
            {t('inventory.movementsPage.filteredByOrder', {
              orderId: filters.refId.slice(-6),
            })}
            <button
              type="button"
              className="movement-ref-filter-clear"
              onClick={() => setFilter('refId', '')}
              aria-label={t('inventory.movementsPage.clearOrderFilter')}
            >
              <X size={14} aria-hidden="true" />
            </button>
          </span>
        )}
        {hasFilters && (
          <button
            type="button"
            className="btn btn-ghost"
            onClick={() => {
              setFilters(EMPTY_FILTERS);
              setViewRestaurantId('');
            }}
          >
            {t('inventory.movementsPage.clearFilters')}
          </button>
        )}
      </div>

      <div className="movements-list-container">
        {loading ? (
          <div className="loading">{t('inventory.movementsPage.loading')}</div>
        ) : movements.length === 0 ? (
          <div className="empty-state">
            <Archive size={48} />
            <p>{t('inventory.movementsPage.empty')}</p>
          </div>
        ) : (
          <>
            {kardex && (
              <div className="kardex-summary">
                <h3>{t('inventory.movementsPage.kardex.title', { item: selectedItem.name })}</h3>
                <div className="kardex-figures">
                  <div>
                    <span className="kardex-label">
                      {t(
                        kardexTruncatedByPaging
                          ? 'inventory.movementsPage.kardex.openingLoaded'
                          : 'inventory.movementsPage.kardex.opening'
                      )}
                    </span>
                    <strong>{kardex.opening ?? '-'}</strong>
                  </div>
                  <div>
                    <span className="kardex-label">
                      {t(
                        kardexTruncatedByPaging
                          ? 'inventory.movementsPage.kardex.netLoaded'
                          : 'inventory.movementsPage.kardex.net'
                      )}
                    </span>
                    <strong className={kardex.net < 0 ? 'text-red' : 'text-green'}>
                      {kardex.net > 0 ? '+' : ''}
                      {kardex.net}
                    </strong>
                  </div>
                  <div>
                    <span className="kardex-label">
                      {t('inventory.movementsPage.kardex.closing')}
                    </span>
                    <strong>{kardex.closing ?? '-'}</strong>
                  </div>
                  <div>
                    <span className="kardex-label">
                      {t('inventory.movementsPage.kardex.currentStock')}
                    </span>
                    <strong>{kardex.currentStock ?? '-'}</strong>
                  </div>
                </div>
                {kardex.balances === true && (
                  <p className="kardex-check kardex-check--ok">
                    {t('inventory.movementsPage.kardex.balances')}
                  </p>
                )}
                {kardex.balances === false && (
                  <p className="kardex-check kardex-check--off">
                    {t('inventory.movementsPage.kardex.doesNotBalance')}
                  </p>
                )}
                {kardex.balances === null && (
                  <p className="text-muted small">
                    {t(
                      kardexTruncatedByPaging
                        ? 'inventory.movementsPage.kardex.paginated'
                        : 'inventory.movementsPage.kardex.filtered'
                    )}
                  </p>
                )}
              </div>
            )}
            <div className="table-responsive">
              <table className="movements-table">
                <thead>
                  <tr>
                    <th>{t('inventory.movementsPage.date')}</th>
                    <th>{t('inventory.movementsPage.type')}</th>
                    <th>{t('inventory.movementsPage.product')}</th>
                    <th>{t('inventory.movementsPage.quantity')}</th>
                    <th>{t('inventory.movementsPage.previousStock')}</th>
                    <th>{t('inventory.movementsPage.newStock')}</th>
                    <th>{t('inventory.movementsPage.reason')}</th>
                    <th>{t('inventory.movementsPage.user')}</th>
                  </tr>
                </thead>
                <tbody>
                  {movements.map((movement) => (
                    <tr key={`${movement.PK}|${movement.SK}`}>
                      <td>{new Date(movement.CreatedAt).toLocaleString()}</td>
                      <td>
                        <div className={`movement-type-badge ${typeBadgeClass(movement.Type)}`}>
                          {getMovementIcon(movement.Type)}
                          <span>{getMovementLabel(movement.Type)}</span>
                        </div>
                      </td>
                      <td className="font-medium">{getItemName(movement.ItemId)}</td>
                      <td className={movement.Quantity > 0 ? 'text-green' : 'text-red'}>
                        {movement.Quantity > 0 ? '+' : ''}
                        {movement.Quantity} {getItemUnit(movement.ItemId)}
                      </td>
                      <td className="text-secondary">{movement.StockBefore}</td>
                      <td className="font-bold">{movement.StockAfter}</td>
                      <td className="text-secondary movement-reason">{renderReason(movement)}</td>
                      <td className="text-sm text-gray">
                        {movement.UserId || t('inventory.movementsPage.system')}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
        {!loading && nextToken && (
          <div className="load-more">
            <button
              type="button"
              className="btn btn-ghost"
              onClick={loadMore}
              disabled={loadingMore}
            >
              {loadingMore
                ? t('inventory.movementsPage.loading')
                : t('inventory.movementsPage.loadMore')}
            </button>
          </div>
        )}
      </div>

      <CreateMovementModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSave={handleCreateMovement}
        items={items}
        restaurants={restaurants}
        currentRestaurantId={currentRestaurantId}
        initialItemId={filters.itemId}
      />

      {isTransferOpen && (
        <TransferStockModal
          restaurants={activeRestaurants}
          catalogItems={items}
          stockByRestaurant={
            currentRestaurantId
              ? {
                  [currentRestaurantId]: Object.fromEntries(
                    items.map((item) => [item.itemId, Number(item.stock) || 0])
                  ),
                }
              : {}
          }
          initialFromRestaurantId={currentRestaurantId || ''}
          initialItems={filters.itemId ? [{ itemId: filters.itemId }] : []}
          onClose={() => setIsTransferOpen(false)}
          onTransferred={handleTransferred}
        />
      )}
    </div>
  );
}

export default InventoryMovementsPage;
